import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import MainPageDialog from "../../../../../components/dialog";
import { InputField } from "../../../../../components/reactHookFormFields/inputField";
import { useGroupChatContext } from "../../../../../context/groupchatContext";
import DualListBoxLocations from "../../../../locationCheckin/DualListBoxLocations";
import { useGetNurseLocationsQuery } from "../../../../../services/location_services";
import { useGetChatGroupLocationsQuery, useSyncCheckinCheckoutMutation } from "../../../../../services/appointmentService";

function AddFacilityGroupDialog({
  setShowAddDialog = () => {},
  selectedGroup = null,
  setSelectedGroup = () => {},
  onSubmitGroup = async () => false,
  isSavingGroup = false,
}) {

    const groupChatContext = useGroupChatContext();
    const { authData } = useMemo(() => ({
        authData: groupChatContext.authData,
    }));

   const [locationProcessing, setLocationProcessing] = useState("");

  
    const isEditMode = !!selectedGroup;
    const { data: providerLocations, isLoading, isFetching} = useGetChatGroupLocationsQuery({ user_id: authData?.id }, { refetchOnMountOrArgChange: true, skip: !authData?.is_provider });
    const { data: nurseLocations, isLoading: nurseLocationsLoading, isFetching: refetchNurseLocations} = useGetNurseLocationsQuery({nurse_id: authData?.id}, { refetchOnMountOrArgChange: true, skip: authData?.is_provider });

    const practiceLocations = useMemo(() => {
        return authData?.is_provider ?  providerLocations || [] :nurseLocations?.data || [];
    }, [nurseLocations?.data, providerLocations, authData?.is_provider]);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setValue,
    } = useForm({
        defaultValues: {
            name: "",
            locations: [],
        },
    });

    const watchedLocations = useWatch({ control, name: "locations" });
    const dualListFilteredLocations = useMemo(() => {
        const selectedIds = new Set(
            (watchedLocations || []).filter(Boolean)
        );
        return (practiceLocations || []).map((loc) => ({
            ...loc,
            checked_in: selectedIds.has(loc.id),
        }));
    }, [practiceLocations, watchedLocations]);


    const syncLocationsFromDualList = async ({allCheckedInSelections, previousCheckedInSelections}) => {
        setLocationProcessing("syncing");

        const checkedInLocationIds = allCheckedInSelections.filter(selection => !selection.is_group).map(selection => selection.id);
        setValue("locations", checkedInLocationIds);
        setLocationProcessing("");
    };

    useEffect(() => {
        if (isEditMode && selectedGroup) {
            setValue("name", selectedGroup?.name || "");
            const selectedLocationIds = Array.isArray(selectedGroup?.locations)
                ? selectedGroup.locations.map((loc) => loc?.id).filter(Boolean)
                : [];
            setValue("locations", selectedLocationIds);
            return;
        }

        reset({
            name: "",
            locations: [],
        });
    }, [isEditMode, selectedGroup, practiceLocations, setValue, reset]);

    const handleCloseDialog = () => {
        setShowAddDialog(false);
        setSelectedGroup(null);
        reset({
            name: "",
            locations: [],
        });
    };

    const onSubmitForm = async (values) => {

        const selectedLocationIds = (values?.locations || [])
        .filter(Boolean);

        if (!selectedLocationIds.length) {
            toast.error("Please select at least one location");
            return;
        }

        const payload = {
            id: selectedGroup?.id,
            name: values?.name?.trim(),
            location_ids: selectedLocationIds.join(","),
        };

        const isSuccess = await onSubmitGroup(payload);
        if (isSuccess) {
            handleCloseDialog();
        }
    };

    const footerList = [
        {
            title: "Close",
            action: handleCloseDialog,
            type: "cancel",
            disabled: isSubmitting || isSavingGroup,
        },
        {
            title: isEditMode ? "Update" : "Save",
            action: () => handleSubmit(onSubmitForm)(),
            type: isEditMode ? "update" : "save",
            disabled: isSubmitting || isSavingGroup,
            isDisabled: isSubmitting || isSavingGroup,
            loading: isSubmitting || isSavingGroup,
        },
    ];

    return (
        <MainPageDialog
            title={isEditMode ? "Edit Facility Location Group" : "Add Facility Location Group"}
            footerList={footerList}
            onCancelHandler={handleCloseDialog}
            modalSize="lg"
        >
            <div className="flex flex-col w-full my-3 min-w-0">
                <form
                    id="add_facility_group_form"
                    onSubmit={handleSubmit(onSubmitForm)}
                    className="flex flex-col gap-4"
                >
                    <div className="min-w-0 max-w-md">
                        <InputField
                            name="name"
                            aria-label="Group Name"
                            title="Group Name"
                            control={control}
                            errors={errors}
                            autoComplete="off"
                            rules={{ required: { value: true, message: "Group name is required" } }}
                        />
                    </div>
                    <div className="min-w-0 w-full">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Locations</p>
                        <DualListBoxLocations
                            filteredLocations={dualListFilteredLocations}
                            locationChatProcessingId={locationProcessing}
                            onSyncCheckinCheckout={syncLocationsFromDualList}
                            isFetching={false}
                            control={control}
                            setValue={setValue}
                        />
                    </div>
                </form>
            </div>
        </MainPageDialog>
    );
}

export default AddFacilityGroupDialog;
